import { Outlet, Link } from 'react-router-dom';
import { ShoppingCart, Menu, X } from 'lucide-react';
import { useState } from 'react';
import { useCart } from '@/context/CartContext';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import CartSheet from '@/components/CartSheet';
import ContactWidget from '@/components/ContactWidget';
import VisitorCounter from '@/components/VisitorCounter';
import DesktopNav from '@/components/DesktopNav';
import MobileNav from '@/components/MobileNav';

export default function Layout() {
  const { cartCount } = useCart();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  return (
    <div className="min-h-screen flex flex-col">
      {/* Navigation */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-background/85 backdrop-blur-xl border-b border-white/5">
        <div className="container-custom">
          <nav className="flex items-center justify-between gap-6 h-16 md:h-20">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-3 shrink-0" data-testid="nav-logo">
              <img 
                src="/grad-fox.png" 
                alt="Grad P Fawkes"
                className="w-10 h-10 md:w-12 md:h-12 object-contain"
              />
              <div className="hidden sm:block leading-tight">
                <p className="text-base md:text-lg font-bold text-foreground tracking-tight">Grad P Fawkes</p>
                <p className="text-[10px] md:text-xs text-muted-foreground tracking-[0.2em] uppercase">The Modern Gnostic 1.6™</p>
              </div>
            </Link>

            <DesktopNav />

            {/* Right side */}
            <div className="flex items-center gap-2 md:gap-3 shrink-0">
              <Link
                to="/donate"
                className="hidden lg:inline-flex px-5 py-2 bg-accent text-accent-foreground rounded-full font-semibold text-sm hover:bg-accent/90 transition-colors"
                data-testid="nav-donate"
              >
                Donate
              </Link>
              <CartSheet>
                <Button variant="ghost" size="icon" className="relative" data-testid="cart-button">
                  <ShoppingCart className="w-5 h-5" />
                  {cartCount > 0 && (
                    <span className="cart-badge" data-testid="cart-count">{cartCount}</span>
                  )}
                </Button>
              </CartSheet>

              <Button
                variant="ghost"
                size="icon"
                className="lg:hidden"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                aria-label="Toggle menu"
                data-testid="mobile-menu-toggle"
              >
                {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
              </Button>
            </div>
          </nav>
        </div>

        {mobileMenuOpen && <MobileNav onNavigate={() => setMobileMenuOpen(false)} />}
      </header>

      {/* Main Content */}
      <main className="flex-1 pt-16 md:pt-20">
        <Outlet />
      </main>

      {/* Contact Widget - appears on all pages */}
      <ContactWidget />

      {/* Footer */}
      <footer className="border-t border-border/50 bg-card/50">
        <div className="container-custom py-12 md:py-16">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {/* Brand */}
            <div className="md:col-span-2">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-full bg-primary flex items-center justify-center">
                  <span className="text-2xl font-black text-white" style={{ fontFamily: 'Playfair Display' }}>G</span>
                </div>
                <div>
                  <p className="text-lg font-bold">Grad P Fawkes™</p>
                  <p className="text-xs text-muted-foreground">The Modern Gnostic 1.6™</p>
                </div>
              </div>
              <p className="text-muted-foreground text-sm max-w-md">
                Satirical news, original music, fairytale adaptations, and premium products. 
                Where sociology meets storytelling.
              </p>
            </div>

            {/* Links */}
            <div>
              <h4 className="font-bold mb-4 text-sm uppercase tracking-wider text-muted-foreground">Video Content</h4>
              <ul className="space-y-2">
                <li><a href="https://www.youtube.com/playlist?list=PLn8UbQXnz_ExF9R4UwNlqHAHF-d7OM24V" target="_blank" rel="noopener noreferrer" className="footer-link text-sm">The Who Cares News</a></li>
                <li><a href="https://www.youtube.com/playlist?list=PLn8UbQXnz_EwxLHHytTn17kdp69kZBNvn" target="_blank" rel="noopener noreferrer" className="footer-link text-sm">GradTrax Music</a></li>
                <li><a href="https://www.youtube.com/playlist?list=PLn8UbQXnz_EwRgkLIlru3qUNH_7XQs8kf" target="_blank" rel="noopener noreferrer" className="footer-link text-sm">WTFairytale</a></li>
                <li><a href="https://www.youtube.com/playlist?list=PLn8UbQXnz_EwC1Iev4OTQrYme4XpY9_O7" target="_blank" rel="noopener noreferrer" className="footer-link text-sm">Social Science Courses</a></li>
              </ul>
            </div>

            {/* Shopping */}
            <div>
              <h4 className="font-bold mb-4 text-sm uppercase tracking-wider text-muted-foreground">Shopping</h4>
              <ul className="space-y-2">
                <li><Link to="/shop" className="footer-link text-sm">Merch Shop</Link></li>
                <li><Link to="/cocoalife" className="footer-link text-sm">CocoaLife™</Link></li>
                <li><a href="https://open.spotify.com/artist/2m8OD1Y5PSQmd5mBPQvR3r" target="_blank" rel="noopener noreferrer" className="footer-link text-sm">On Spotify</a></li>
                <li><a href="https://www.amazon.com/vdp/0ce1e9c32b2a44af8f885a7928faf536?product=B0FW7QRFJQ&ref=cm_sw_cp_r_ib_dt_vcAtgGIImsgEM" target="_blank" rel="noopener noreferrer" className="footer-link text-sm">Oumaumau Books</a></li>
              </ul>
            </div>
          </div>

          <Separator className="my-8" />

          <div className="flex flex-col md:flex-row items-center justify-between gap-4 text-sm text-muted-foreground">
            <p>© 2024 Grad P Fawkes™. All rights reserved.</p>
            <div className="flex items-center gap-4">
              <VisitorCounter />
              <p>MGN1.6™ • GradTrax™ • The Who Cares News™ • WTFairytale™</p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
